import React, { useState, useEffect, useRef } from "react";
import PropTypes from "prop-types";
import queryString from "query-string";

import createFakeTwitchHelper from "../../lib/create-fake-twitch-helper";
import {
  TwitchAuthContext,
  TwitchBitsContext,
  TwitchBroadcastContext,
  TwitchContextContext,
} from "../../contexts";
import FakeHelperOptionsModal from "../FakeHelperOptionsModal/FakeHelperOptionsModal.js";

const queryParams = queryString.parse(window.location.search);

const TwitchHelper = ({ useFakeTwitchHelperAPI = false, children }) => {
  const canInitialiseFakeHelper = [
    queryParams.channel_id,
    queryParams.user_id,
  ].every(Boolean);
  const [isInitialised, setIsInitialised] = useState(false);
  const [auth, setAuth] = useState(null);
  const [context, setContext] = useState(null);
  const [bitsProducts, setBitsProducts] = useState([]);
  const listenersByTarget = useRef({});
  const [isFakeHelperOptionsModalVisible, setIsFakeHelperOptionsModalVisible] =
    useState(useFakeTwitchHelperAPI && !canInitialiseFakeHelper);

  const addPubSubEventListener = (target, callback) => {
    if (!listenersByTarget.current[target]) {
      listenersByTarget.current[target] = [];
    }

    if (!listenersByTarget.current[target].find(callback)) {
      listenersByTarget.current[target].push(callback);
    }
  };

  const removePubSubEventListener = (target, callback) => {
    if (!listenersByTarget.current[target]) {
      return;
    }

    listenersByTarget.current[target] = listenersByTarget.current[
      target
    ].filter((cb) => cb !== callback);
  };

  useEffect(() => {
    const handler = (e) => {
      if (e.key === "F2") {
        setIsFakeHelperOptionsModalVisible(true);
      }
    };

    window.addEventListener("keyup", handler);

    return () => {
      window.removeEventListener("keyup", handler);
    };
  }, []);

  useEffect(() => {
    (async () => {
      let twitchHelper = window.Twitch.ext;

      if (useFakeTwitchHelperAPI && !canInitialiseFakeHelper) {
        return;
      }

      if (useFakeTwitchHelperAPI) {
        const fakeTwitchHelper = await createFakeTwitchHelper();
        twitchHelper = fakeTwitchHelper;

        window.Twitch = {
          ext: fakeTwitchHelper,
        };
      }

      if (twitchHelper.features.isBitsEnabled) {
        const products = await twitchHelper.bits.getProducts();
        setBitsProducts(products);
      }

      twitchHelper.listen(
        "broadcast",
        async function (target, contentType, data) {
          const { event, payload } = JSON.parse(data);
          console.log("[Twitch broadcast]:", { event, payload });
          const listeners = listenersByTarget.current[event] || [];
          listeners.forEach((listener) => listener(payload));
        }
      );

      twitchHelper.onAuthorized((auth) => {
        setAuth(auth);
        setIsInitialised(true);
        //
        //try {
        //  Sentry.setUser({ id: auth.userId });
        //  Sentry.setTag("channelId", auth.channelId);
        //  const version = window.pathname.split("/")[2];
        //  Sentry.setTag("extensionVersion", version || "NO_VERSION");
        //} catch (err) {}
      });

      twitchHelper.onContext((context) => {
        setContext(context);
      });
    })();
  }, []);

  if (isFakeHelperOptionsModalVisible) {
    return <FakeHelperOptionsModal />;
  }

  if (!isInitialised) {
    return null;
  }

  const broadcastContextValue = {
    addPubSubEventListener,
    removePubSubEventListener,
  };

  return (
    <TwitchAuthContext.Provider value={auth}>
      <TwitchContextContext.Provider value={context}>
        <TwitchBroadcastContext.Provider value={broadcastContextValue}>
          <TwitchBitsContext.Provider value={{ products: bitsProducts }}>
            {children}
          </TwitchBitsContext.Provider>
        </TwitchBroadcastContext.Provider>
      </TwitchContextContext.Provider>
    </TwitchAuthContext.Provider>
  );
};

TwitchHelper.propTypes = {
  useFakeTwitchHelperAPI: PropTypes.bool,
};

export default TwitchHelper;
